/**
 * useGabberSmsNumber — env-aware Gabber SMS number.
 *
 * Reads the active outbound number from public runtime config so staging and
 * production can inject their own numbers without a client fetch.
 *
 * Usage:
 * ```vue
 * <script setup lang="ts">
 * const { e164, display } = useGabberSmsNumber();
 * </script>
 *
 * <template>
 *   <a :href="`tel:${e164}`">{{ display }}</a>
 *   <p>{{ $t('some.key', { smsNumber: display }) }}</p>
 * </template>
 * ```
 */

const PROD_FALLBACK_E164 = '+18664023456';

function formatE164(e164: string): string {
  // +1NXXNXXXXXX → +1 (NXX) NXX-XXXX
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return e164; // non-US / unexpected format → return as-is
}

export function useGabberSmsNumber() {
  const config = useRuntimeConfig();

  const e164 = computed<string>(() => {
    const configured = String(config.public.gabberSmsNumber || '').trim();
    return configured || PROD_FALLBACK_E164;
  });
  const display = computed<string>(() => formatE164(e164.value));

  return { e164, display };
}
